/**
 * AccountSecurityCheckup — Widget compact à intégrer dans le dashboard utilisateur.
 *
 * Affiche :
 *  - Score sécurité du compte sur 100
 *  - 3-4 checks (2FA, password age, logins récents)
 *  - CTAs vers les pages d'amélioration
 *
 * Consomme : GET /api/auth/security-status
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, CheckCircle2, AlertCircle, ChevronRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountSecurityCheckup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/security-status`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-slate-200/60 flex items-center gap-3" data-testid="security-checkup-loading">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Vérification de votre compte…</span>
      </div>
    );
  }
  if (!data) return null;

  const score = data.score || 0;
  const scoreColor = score >= 90 ? "text-emerald-600" : score >= 70 ? "text-[#C9A961]" : "text-rose-600";
  const scoreBg = score >= 90 ? "bg-emerald-50 border-emerald-200" : score >= 70 ? "bg-[#C9A961]/10 border-[#C9A961]/30" : "bg-rose-50 border-rose-200";
  const ScoreIcon = score >= 90 ? ShieldCheck : score >= 70 ? ShieldCheck : ShieldAlert;

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden" data-testid="security-checkup">
      <div className={`${scoreBg} px-5 py-4 border-b border-slate-200/60 flex items-center gap-3`}>
        <ScoreIcon className={`w-6 h-6 ${scoreColor}`} />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-slate-500">Sécurité du compte</p>
          <p className="font-serif text-2xl text-slate-900 leading-tight">
            <span className={scoreColor}>{score}</span><span className="text-slate-400 text-base">/100</span>
          </p>
        </div>
        <Link to="/security" className="text-xs text-slate-600 hover:text-[#0B3A2A] hidden sm:flex items-center gap-1" data-testid="security-checkup-learn">
          En savoir plus <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      <ul className="divide-y divide-slate-100">
        {(data.checks || []).map((c) => (
          <li key={c.key} className="px-5 py-3 flex items-start gap-3" data-testid={`security-check-${c.key}`}>
            {c.ok ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm ${c.ok ? "text-slate-700" : "text-slate-900 font-medium"}`}>{c.label}</p>
            </div>
            {c.action && !c.ok && (
              <Link to={c.action} className="text-xs text-[#C9A961] hover:text-[#0B3A2A] shrink-0" data-testid={`security-check-action-${c.key}`}>
                Corriger →
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
